import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserWelcome from './UserWelcome';
import { USER_ROLES } from '../../constants/authConstants';

const NavigationCard = ({ title, description, icon, path, color }) => {
  return (
    <Link to={path} className="block">
      <div className={`
        card card-hover p-4 cursor-pointer group
      `}>
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${color} group-hover:scale-110 transition-transform`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-text-primary mb-1 group-hover:text-accent transition-colors text-sm">
              {title}
            </h3>
            <p className="text-text-secondary text-xs leading-relaxed line-clamp-3">
              {description}
            </p>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

const ComingSoonCard = ({ title, description, icon, color }) => {
  return (
    <div className={`
      card p-4 opacity-60 cursor-not-allowed
    `}>
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary mb-1 text-sm">
            {title}
          </h3>
          <p className="text-text-secondary text-xs leading-relaxed line-clamp-2">
            {description}
          </p>
          <div className="mt-2">
            <span className="status-warning text-xs px-2 py-1">
              Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { getUserRole } = useAuth();
  const userRole = getUserRole();

  const getNavigationItems = () => {
    // Admin navigation items
    const adminItems = [
      {
        title: 'User Management',
        description: 'Manage department user registrations, approvals, and account settings',
        path: '/admin/users',
        color: 'bg-blue-100',
        icon: (
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" 
            />
          </svg>
        )
      },
      {
        title: 'Analytics Dashboard',
        description: 'View system analytics, user statistics, and department insights',
        path: '/analytics/users',
        color: 'bg-emerald-100',
        type: 'coming-soon',
        icon: (
          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" 
            />
          </svg>
        )
      },
      {
        title: 'System Reports',
        description: 'Generate and view comprehensive system reports and data exports',
        path: '/reports/system',
        color: 'bg-purple-100',
        type: 'coming-soon',
        icon: (
          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" 
            />
          </svg>
        )
      },
      {
        title: 'Sector Overview',
        description: 'Monitor activity and statistics across all government sectors',
        path: '/analytics/sectors',
        color: 'bg-accent-subtle',
        type: 'coming-soon',
        icon: (
          <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M3.75 21h16.5M4.5 3h15l-.75 18H5.25L4.5 3zM9 9l3 3-3 3m5-6h.01" 
            />
          </svg>
        )
      }
    ];

    // Department user navigation items
    const departmentItems = [
      {
        title: 'My Reports',
        description: 'Access and manage your department-specific reports and submissions',
        path: '/reports/my-reports',
        color: 'bg-green-100',
        type: 'coming-soon',
        icon: (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h4.125M8.25 8.25l2.25 2.25-2.25 2.25M13.5 8.25l2.25 2.25-2.25 2.25" 
            />
          </svg>
        )
      },
      {
        title: 'Data Submissions',
        description: 'Submit and track your department data and compliance reports',
        path: '/submissions',
        color: 'bg-cyan-100',
        type: 'coming-soon',
        icon: (
          <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" 
            />
          </svg>
        )
      }
    ];

    // Return items based on role
    switch (userRole) {
      case USER_ROLES.ADMIN:
        return adminItems;
      case USER_ROLES.DEPARTMENT:
        return departmentItems;
      default:
        return [];
    }
  };

  const getWelcomeDescription = () => {
    const descriptions = {
      [USER_ROLES.ADMIN]: 'Manage users, monitor system activity, and oversee StateSync operations.',
      [USER_ROLES.DEPARTMENT]: 'Access your department tools and contribute to government data synchronization.',
      [USER_ROLES.PLANNING_OFFICIAL]: 'Review budget proposals, allocations and clearances across all departments.',
      [USER_ROLES.DONER]: 'Review budget proposals, allocations and clearances across all departments.'
    };
    return descriptions[userRole] || 'Welcome to StateSync Government Portal.';
  };

  const getPortalTitle = () => {
    const titles = {
      [USER_ROLES.ADMIN]: 'Administration Panel',
      [USER_ROLES.DEPARTMENT]: 'Department Portal',
      [USER_ROLES.PLANNING_OFFICIAL]: 'Planning Portal',
      [USER_ROLES.DONER]: 'DoNER Portal'
    };
    return titles[userRole] || 'StateSync Portal';
  };

  return (
    <div className="space-y-4">
      {/* User Welcome Section */}
      <UserWelcome />

      {/* System Overview - Compact Design */}
      <div className="card p-5">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-text-primary mb-1">
            {getPortalTitle()}
          </h2>
          <p className="text-text-secondary text-sm">
            {getWelcomeDescription()}
          </p>
        </div>

        {/* Navigation Grid - Compact Layout */}
        <div className={`grid ${
          userRole === USER_ROLES.ADMIN 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2' 
            : 'grid-cols-1 md:grid-cols-2'
        } gap-3`}>
          {getNavigationItems().map((item, index) => (
            item.type === 'coming-soon' ? (
              <ComingSoonCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                color={item.color}
              />
            ) : (
              <NavigationCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                path={item.path}
                color={item.color}
              />
            )
          ))}
        </div>

        {/* Role-specific notice */}
        {userRole === USER_ROLES.DEPARTMENT && (
          <div className="mt-4 p-3 bg-info-subtle border border-border-primary rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-info mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-xs text-info">
                <p className="font-medium mb-0.5">Department Access Active</p>
                <p className="text-xs text-text-secondary">
                  You have access to department-specific tools and reporting features. More functionality will be available soon.
                </p>
              </div>
            </div>
          </div>
        )}

        {userRole === USER_ROLES.ADMIN && (
          <div className="mt-4 p-3 bg-success-subtle border border-border-primary rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-success mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="text-xs text-success">
                <p className="font-medium mb-0.5">Admin Panel Ready</p>
                <p className="text-xs text-text-secondary">
                  Full administrative access is available. You can manage users, view analytics, and monitor system operations.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;